import { canUseDOM, getWindow } from '@surfline/web-common';

import { redirectToNotFound } from 'actions/status';
import { getNearestSpot, fetchLocationMapView } from 'common/api/maps';
import canUseNavigator from 'common/canUseNavigator';
import { DEFAULT_SURFLINE_LOCATION } from 'common/constants';
import { getUserLocationByGPS, getUserLocationByIP } from 'common/location';
import { mapPath } from 'utils/urls';

import { fetchSpotMapData } from './spotMapData';

export const SET_MAP_LOCATION = 'SET_MAP_LOCATION';
export const RERENDER_MAP = 'RERENDER_MAP';

export const FETCH_LOCATION_VIEW = 'FETCH_LOCATION_VIEW';
export const FETCH_LOCATION_VIEW_SUCCESS = 'FETCH_LOCATION_VIEW_SUCCESS';
export const FETCH_LOCATION_VIEW_FAILURE = 'FETCH_LOCATION_VIEW_FAILURE';

export const getUserLocation = async () => {
  let location = DEFAULT_SURFLINE_LOCATION;
  if (canUseNavigator) {
    try {
      location = await getUserLocationByGPS();
    } catch (_err) {
      location = await getUserLocationByIP();
    }
  } else {
    location = await getUserLocationByIP();
  }

  return location;
};

/**
 * Safer version of getUserLocation that doesn't cause race conditions
 * Use this version when you need location but don't want multiple simultaneous calls
 */
export const getSafeUserLocation = async () => {
  let location = DEFAULT_SURFLINE_LOCATION;

  // Try IP location first - it's fast and reliable
  try {
    location = await getUserLocationByIP();
  } catch (_err) {
    // Keep default location if IP fails
  }

  // Only try GPS if we're in a navigation context
  if (canUseNavigator && navigator?.geolocation) {
    try {
      const gpsLocation = await getUserLocationByGPS();

      // Only use GPS if it provides significantly different location (more than ~1km difference)
      const latDiff = Math.abs(gpsLocation.center.lat - location.center.lat);
      const lonDiff = Math.abs(gpsLocation.center.lon - location.center.lon);
      const significantDifference = latDiff > 0.01 || lonDiff > 0.01; // roughly 1km

      if (significantDifference) {
        location = gpsLocation;
      }
    } catch (_err) {
      // Keep IP or default location if GPS fails
    }
  }

  return location;
};

export const shouldRerender = () => ({
  type: RERENDER_MAP,
});

/**
 * @param {import('../propTypes/map/location').Location} location
 * @param {import('next/router').NextRouter} router
 * @param {boolean} [syncUrl]
 * @param {boolean} [storeLocationInSession]
 */
export const setMapLocation =
  ({ center, zoom }, router, syncUrl = false, storeLocationInSession = false) =>
  (dispatch) => {
    const win = getWindow();

    if (syncUrl) {
      const newPath = `${mapPath(center, zoom)}`;
      if (canUseDOM && newPath !== router.asPath && router.isReady) {
        // Add error handling for router.replace to prevent abort errors
        try {
          router.replace(newPath);
        } catch (err) {
          // Silently handle router abort errors which are common during fast navigation
          if (!err.cancelled && !err.message?.includes('Abort')) {
            console.error('Router replace error:', err);
          }
        }
      }
    }

    // Only store in session storage if explicitly requested
    if (storeLocationInSession === true) {
      try {
        win.sessionStorage.setItem('slMapLocation', JSON.stringify({ center, zoom }));
      } catch (_err) {
        console.error('Session storage not available.');
      }
    }

    dispatch({
      type: SET_MAP_LOCATION,
      center,
      zoom,
    });
  };

/**
 * @param {import('next/router').NextRouter} router
 * @param {boolean} [syncUrl]
 * @param {boolean} [storeLocationInSession]
 */
export const goToUsersLocation =
  (router, syncUrl = false, storeLocationInSession = false) =>
  async (dispatch) => {
    let position;

    try {
      position = await getUserLocation();
    } catch (_err) {
      position = DEFAULT_SURFLINE_LOCATION;
    }

    dispatch(setMapLocation({ ...position }, router, syncUrl, storeLocationInSession));
  };

/**
 * @param {import('next/router').NextRouter} router
 * @param {boolean} [syncUrl]
 * @param {boolean} [storeLocationInSession]
 * @returns
 */
export const goToUsersNearestSpot =
  (router, syncUrl = false, storeLocationInSession = false) =>
  async (dispatch) => {
    let finalPosition = DEFAULT_SURFLINE_LOCATION;
    let isLocationResolved = false;
    // Try IP location first - fast and reliable
    try {
      const location = await getUserLocationByIP();
      const nearest = await getNearestSpot({
        ...location.center,
      });
      finalPosition = {
        center: {
          lat: nearest.spot.lat,
          lon: nearest.spot.lon,
        },
        zoom: location.zoom,
      };
      isLocationResolved = true;
    } catch (_err) {
      // Keep default location as fallback
    }

    // If IP location succeeded, dispatch the result immediately
    if (isLocationResolved) {
      dispatch(setMapLocation({ ...finalPosition }, router, syncUrl, storeLocationInSession));
    }

    // Try GPS location for more accuracy (if available and if not already resolved)
    if (navigator?.geolocation) {
      try {
        const location = await getUserLocationByGPS();
        const nearest = await getNearestSpot({
          ...location.center,
        });
        const gpsPosition = {
          center: {
            lat: nearest.spot.lat,
            lon: nearest.spot.lon,
          },
          zoom: location.zoom,
        };

        // Only update if GPS provides significantly different location (more than ~1km difference)
        const latDiff = Math.abs(gpsPosition.center.lat - finalPosition.center.lat);
        const lonDiff = Math.abs(gpsPosition.center.lon - finalPosition.center.lon);
        const significantDifference = latDiff > 0.01 || lonDiff > 0.01; // roughly 1km

        if (!isLocationResolved || significantDifference) {
          finalPosition = gpsPosition;
          dispatch(setMapLocation({ ...finalPosition }, router, syncUrl, storeLocationInSession));
        }
      } catch (_err) {
        // If we haven't resolved any location yet, fall back to default
        if (!isLocationResolved) {
          dispatch(setMapLocation({ ...finalPosition }, router, syncUrl, storeLocationInSession));
        }
      }
    } else if (!isLocationResolved) {
      // No GPS available and IP failed, use default
      dispatch(setMapLocation({ ...finalPosition }, router, syncUrl, storeLocationInSession));
    }
  };

/**
 * @param {import('../propTypes/map/location').Location["center"]} center
 * @param {import('next/router').NextRouter} router
 * @param {boolean} [syncUrl]
 * @param {boolean} [storeLocationInSession]
 * @returns
 */
export const zoomIntoLocation =
  (center, router, syncUrl = false, storeLocationInSession = false) =>
  (dispatch, state) => {
    dispatch(
      setMapLocation(
        {
          center,
          zoom: state().map.location.zoom + 1,
        },
        router,
        syncUrl,
        storeLocationInSession,
      ),
    );
  };

/**
 * @param {string | number} geonameId
 */
export const fetchLocationView =
  (geonameId = null) =>
  async (dispatch) => {
    dispatch({
      type: FETCH_LOCATION_VIEW,
    });
    try {
      if (parseInt(geonameId, 10)) {
        const response = await fetchLocationMapView(geonameId);
        const { taxonomy, travelContent, boundingBox, breadCrumbs, siblings, url } = response;
        await dispatch({
          type: FETCH_LOCATION_VIEW_SUCCESS,
          boundingBox,
          taxonomy,
          travelContent,
          breadCrumbs,
          siblings,
          url,
        });
        await dispatch(fetchSpotMapData(boundingBox, true));
      } else {
        throw new Error('geonameId is not a number');
      }
    } catch (error) {
      if (error.status === 400) {
        dispatch(redirectToNotFound('The location not be found'));
      }
      dispatch({
        type: FETCH_LOCATION_VIEW_FAILURE,
        error: error.message,
      });
    }
  };
